import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { apiClient } from '../../lib/api-client';
import { Skeleton } from '../ui/Skeleton';
import Editor from 'react-simple-code-editor';
import Prism from 'prismjs';
import 'prismjs/components/prism-yaml';
import 'prismjs/themes/prism-tomorrow.css';
import yaml from 'js-yaml';

interface RBACYamlEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function RBACYamlEditor({ isOpen, onClose, onSuccess }: RBACYamlEditorProps) {
  const [yamlContent, setYamlContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadYaml();
    }
  }, [isOpen]);

  const loadYaml = async () => {
    setLoading(true);
    setError(null);
    setValidationError(null);
    
    try {
      // Fetch raw text, not JSON
      const response = await fetch('/api/admin/yaml-groups/raw', {
        credentials: 'include', // Include session cookie
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load YAML: ${response.statusText}`);
      }
      
      const yamlText = await response.text();
      setYamlContent(yamlText);
    } catch (err: any) {
      setError(err.message || 'Failed to load RBAC YAML');
    } finally {
      setLoading(false);
    }
  };

  const validateYaml = (content: string): boolean => {
    try {
      const parsed = yaml.load(content) as any;
      
      // Validate required structure
      if (!parsed || typeof parsed !== 'object') {
        setValidationError('Invalid YAML: Root must be an object');
        return false;
      }
      
      if (!parsed.groups || !Array.isArray(parsed.groups)) {
        setValidationError('Invalid YAML: "groups" must be an array');
        return false;
      }
      
      // Note: permission_options is now in local permission-schema.yaml, not in this file
      
      setValidationError(null);
      return true;
    } catch (err: any) {
      setValidationError(`Invalid YAML: ${err.message}`);
      return false;
    }
  };

  const handleSave = async () => {
    if (!validateYaml(yamlContent)) {
      return;
    }

    setSaving(true);
    setError(null);
    
    try {
      const response = await fetch('/api/admin/yaml-groups/raw', {
        method: 'POST',
        credentials: 'include', // Include session cookie
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ yaml: yamlContent }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(errorData.error || 'Failed to save YAML');
      }
      
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save RBAC YAML');
    } finally {
      setSaving(false);
    }
  };

  const handleYamlChange = (newValue: string) => {
    setYamlContent(newValue);
    setValidationError(null);
  };

  const highlightYaml = (code: string) => {
    return Prism.highlight(code, Prism.languages.yaml, 'yaml');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent maxWidth="4xl">
        <DialogHeader>
          <DialogTitle>Edit RBAC Configuration</DialogTitle>
          <DialogDescription>
            Edit group permissions, roles, and access control configuration
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {loading && (
            <div className="space-y-2 py-4">
              <Skeleton className="h-64 w-full" />
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-200 mb-4">
              {error}
            </div>
          )}

          {validationError && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-amber-800 dark:text-amber-200 mb-4">
              {validationError}
            </div>
          )}

          {!loading && (
            <div className="relative">
              <div className="bg-gray-900 rounded-lg overflow-hidden border-2 border-gray-700 focus-within:border-blue-500">
                <Editor
                  value={yamlContent}
                  onValueChange={handleYamlChange}
                  highlight={highlightYaml}
                  padding={16}
                  style={{
                    fontFamily: '"Fira Code", "Fira Mono", monospace',
                    fontSize: 14,
                    backgroundColor: '#1e293b',
                    color: '#e2e8f0',
                    minHeight: '500px',
                  }}
                  className="outline-none"
                />
              </div>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving || loading || !!validationError}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

